import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  GENERIC_ERROR_MESSAGE,
  extractErrorMessage,
  isGenericError,
  isFetchBaseQueryError,
  isObject
} from "@/shared/lib"
import { resolveRecognizedErrorMessage } from "@/shared/redux"

interface ShowSuccessOptions {
  /** Adds "Undo" and keeps the toast long enough to reach it. */
  onUndo?: () => void
}

interface ShowErrorOptions {
  /** Adds "Try again"; the toast then stays until the user acts on it or closes it. */
  onRetry?: () => void
}

interface GridWriteErrorDetail {
  code?: unknown
  grid_name?: unknown
  grid_id?: unknown
}

const resolveGridWriteErrorMessage = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("data" in error)) return null

  const data = error.data
  if (typeof data !== "object" || data === null || !("error" in data) || !Array.isArray(data.error)) return null

  const detail: GridWriteErrorDetail | undefined = data.error[0]
  if (typeof detail !== "object" || detail === null) return null

  if (detail.code !== "local_settings_write_redirect" && detail.code !== "local_settings_write_blocked") return null

  const grid =
    typeof detail.grid_name === "string" && detail.grid_name.trim()
      ? detail.grid_name
      : typeof detail.grid_id === "string"
        ? detail.grid_id
        : undefined
  const isBlocked = detail.code === "local_settings_write_blocked"

  return {
    key: isBlocked ? "common:error-local-settings-write-blocked" : "common:error-local-settings-write-redirect",
    fallback: isBlocked
      ? "This setting is managed by grid “{{grid}}”. Ask a grid administrator to change it or turn off sync for this chat."
      : "This setting is managed by grid “{{grid}}”. Edit it in the grid or turn off sync for this chat.",
    grid
  }
}

/** Backend messages are diagnostics. Recognized codes resolve before this check; otherwise only a CUSTOM_ERROR, which
 * frontend query code writes, or a message the error middleware has already translated (`isGenericError: false`)
 * is fit to show instead of the caller's text. */
const isUntranslatedApiError = (error: unknown) =>
  isFetchBaseQueryError(error) &&
  error.status !== "CUSTOM_ERROR" &&
  !(isObject(error.data) && error.data.isGenericError === false)

export interface ToastHandlers {
  showSuccessSave: () => void
  showSuccessUpdate: () => void
  showError: (error: unknown, fallback?: string, options?: ShowErrorOptions) => void
  showSuccess: (message: string, options?: ShowSuccessOptions) => void
  showWarning: (message: string) => void
}

const DEFAULT_DURATION = 1500
const ERROR_DURATION = 4000
const UNDO_DURATION = 6000

export const useToast = (): ToastHandlers => {
  const { t } = useTranslation()

  const showSuccessSave = () => {
    toast.success(t("general_settings_card-settings-saved", "Settings saved"), { duration: DEFAULT_DURATION })
  }

  const showSuccessUpdate = () => {
    toast.success(t("settings-settings_updated", "Settings updated"), { duration: DEFAULT_DURATION })
  }

  const showError = (error: unknown, fallback?: string, options?: ShowErrorOptions) => {
    console.error("[showError]", error)
    let transportMessage: string | undefined
    if (isFetchBaseQueryError(error)) {
      const status = error.status === "PARSING_ERROR" ? error.originalStatus : error.status
      if (status === 413) transportMessage = t("common:error-upload-too-large")
      else if (status === 502) transportMessage = t("common:error-gateway")
      else if (status === 504) transportMessage = t("common:error-gateway-timeout")
      else if (error.status === "PARSING_ERROR") transportMessage = t("common:error-parsing")
    }
    const gridWriteError = resolveGridWriteErrorMessage(error)
    const gridMessage =
      gridWriteError &&
      t(gridWriteError.key, gridWriteError.fallback, {
        grid: gridWriteError.grid ?? t("common:error-settings-grid-unknown", "Settings Grid")
      })
    const extracted =
      transportMessage ??
      gridMessage ??
      resolveRecognizedErrorMessage(error) ??
      (fallback && isUntranslatedApiError(error) ? fallback : extractErrorMessage(error))
    const message =
      !transportMessage && (isGenericError(error) || extracted === GENERIC_ERROR_MESSAGE)
        ? (fallback ?? t("common:error-unknown", "An unexpected error occurred"))
        : extracted
    const onRetry = options?.onRetry
    if (!onRetry) {
      toast.error(message, { duration: ERROR_DURATION })
      return
    }
    toast.error(message, {
      duration: Infinity,
      action: { label: t("common:error-boundary-try-again", "Try again"), onClick: onRetry }
    })
  }

  const showSuccess = (message: string, options?: ShowSuccessOptions) => {
    const onUndo = options?.onUndo
    if (!onUndo) {
      toast.success(message, { duration: DEFAULT_DURATION })
      return
    }
    toast.success(message, {
      duration: UNDO_DURATION,
      action: { label: t("moderation-commands:trg-undo", "Undo"), onClick: onUndo }
    })
  }

  const showWarning = (message: string) => {
    toast.warning(message, { duration: DEFAULT_DURATION })
  }

  return {
    showSuccessSave,
    showSuccessUpdate,
    showError,
    showSuccess,
    showWarning
  }
}
